import React, { useCallback, useEffect, useMemo, useState } from "react";

import ChoiceCard from "../components/ChoiceCard.jsx";
import SearchBar from "../components/SearchBar.jsx";

function make5eToolsUrl(className, subclassName) {
  const cls = className.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "%20");
  const sub = subclassName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "%20");
  return `https://5e.tools/classes.html#${cls}_xphb,state:sub=${sub}_xphb~`;
}

/* ─── Features Modal ─────────────────────────────────────────────── */

function FeaturesModal({ subclass, onClose }) {
  if (!subclass) return null;
  const features = subclass.features || [];

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Close when clicking the backdrop
  const handleBackdrop = useCallback(
    (e) => { if (e.target === e.currentTarget) onClose(); },
    [onClose]
  );

  return (
    <div className="sc-features-overlay" onClick={handleBackdrop} role="dialog" aria-modal="true">
      <div className="sc-features-panel">
        <button className="sc-features-close" onClick={onClose} aria-label="Fechar">×</button>
        <h3 className="sc-features-title">{subclass.name}</h3>
        {subclass.desc && <p className="sc-features-subtitle">{subclass.desc}</p>}

        <div className="sc-features-list">
          {features.map((f, i) => (
            <div key={i} className="sc-feature-item">
              <div className="sc-feature-header">
                <span className="sc-feature-name">{f.name}</span>
                <span className="sc-feature-level">Nível {f.level}</span>
              </div>
              <p className="sc-feature-desc">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Side Detail ─────────────────────────────────────────────────── */

function SubclassDetail({ subclass, className, onShowFeatures }) {
  if (!subclass) return null;
  const url = make5eToolsUrl(className, subclass.name);
  const hasFeatures = (subclass.features || []).length > 0;

  return (
    <div className="side-detail-panel">
      <div className="side-detail-name">{subclass.name}</div>
      {subclass.desc && <p className="side-detail-summary">{subclass.desc}</p>}

      {hasFeatures && (
        <button className="sc-show-features-btn" onClick={onShowFeatures}>
          📜 Mostrar Características
        </button>
      )}

      <a
        className="side-detail-link"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        📖 Ver detalhes completos no 5e.tools
      </a>
    </div>
  );
}

function SubclassEmpty() {
  return (
    <div className="side-empty-panel">
      <div className="side-empty-icon">🛡️</div>
      <div className="side-empty-text">
        Selecione uma subclasse para ver seus detalhes e acessar a página completa no 5e.tools.
      </div>
    </div>
  );
}

export default function SubclassStep({ state, onSelectSubclass }) {
  const [query, setQuery] = useState("");
  const [showFeaturesFor, setShowFeaturesFor] = useState(null);

  const subclasses = state.charClass?.subclasses ?? [];
  const filtered = useMemo(() => {
    if (!query) return subclasses;
    const q = query.toLowerCase();
    return subclasses.filter(
      (s) => s.name.toLowerCase().includes(q) || s.desc?.toLowerCase().includes(q)
    );
  }, [subclasses, query]);

  const selectedSubclass = state.subclassData || null;
  const className = state.charClass?.name || "";

  if (!subclasses.length) {
    return <div className="loading-msg">Selecione uma classe primeiro…</div>;
  }

  return (
    <div className="step-split-layout">
      <div className="step-cards-col">
        <SearchBar value={query} onChange={setQuery} placeholder="Buscar subclasse…" />
        <div className="card-grid card-grid--portrait" id="subclassCards">
          {filtered.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
              Nenhuma subclasse encontrada para "{query}".
            </p>
          ) : (
            filtered.map((subclass) => (
              <ChoiceCard
                key={subclass.name}
                selected={state.subclassData?.name === subclass.name}
                onClick={() => onSelectSubclass(subclass)}
              >
                <div className="card-name">{subclass.name}</div>
              </ChoiceCard>
            ))
          )}
        </div>
      </div>

      <div className="step-info-col">
        {selectedSubclass ? (
          <SubclassDetail
            subclass={selectedSubclass}
            className={className}
            onShowFeatures={() => setShowFeaturesFor(selectedSubclass)}
          />
        ) : (
          <SubclassEmpty />
        )}
      </div>

      {showFeaturesFor && (
        <FeaturesModal
          subclass={showFeaturesFor}
          onClose={() => setShowFeaturesFor(null)}
        />
      )}
    </div>
  );
}
